import { useAppSelector } from '@src/state/hooks';
import { purchasedTier, premiumTierName } from '@src/components/PremiumBadge/tierIdentity';
import { useTranslation } from 'react-i18next';
import { PremiumBadge } from '@src/components/PremiumBadge/PremiumBadge';
import type { ReactNode } from 'react';
import { ChevronRightIcon } from '@src/components/icons/ChevronRightIcon';
import type { BrowserProfileInfo } from '@lub0v/santa-utils';
import { Panel } from '@src/components/Panel/Panel';
import { Button } from '@src/components/Button/Button';
import { ParticipantFace } from '@src/components/faces/ParticipantFace';
import { Spinner } from '@src/components/Spinner/Spinner';
import { Typography } from '@src/components/Typography/Typography';
import { getProfileName, getProfileSummary } from '@src/components/AccountSwitcher/profiles';
import './AccountSwitcher.css';

export type SwitcherBusyAction = 'add' | 'sign-out' | 'sign-out-all' | 'link';

export interface AccountSwitcherProps {
  embedded?: boolean;
  profiles: BrowserProfileInfo[];
  activeUserId: number;
  labels: {
    title: string;
    profile: string;
    guest: string;
    active: string;
    organizer: string;
    noBoxes: string;
    moreOne: string;
    more: (count: number) => string;
    goToAccount: string;
    signOut: string;
    addAccount: string;
    signOutAll: string;
    linkGuest: string;
    switchTo: (name: string) => string;
  };
  busyProfileId?: number;
  busyAction?: SwitcherBusyAction;
  error?: string;
  onSelectProfile: (userId: number) => void;
  onLinkGuest?: (userId: number) => void;
  onGoToAccount: () => void;
  onGoToAdmin?: () => void;
  onAddAccount: () => void;
  onSignOut: () => void;
  onSignOutAll: () => void;
}

function ProfileSurface({ embedded, children }: { embedded: boolean; children: ReactNode }) {
  return embedded ? (
    <Panel padding="none" elevated wide className="account-switcher__profile-panel">
      {children}
    </Panel>
  ) : (
    <>{children}</>
  );
}

function ProfileAvatar({ profile, active }: { profile: BrowserProfileInfo; active?: boolean }) {
  return (
    <span
      className={
        active
          ? 'account-switcher__avatar account-switcher__avatar--active'
          : 'account-switcher__avatar'
      }
    >
      <ParticipantFace card={profile} index={profile.id} size={active ? 'identity' : 'row'} />
    </span>
  );
}

function ProfileDetails({
  profile,
  labels,
  active,
}: {
  profile: BrowserProfileInfo;
  labels: AccountSwitcherProps['labels'];
  active?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const user = useAppSelector(state => state.session.user);
  const tier = user?.id === profile.id ? purchasedTier(user) : undefined;
  const name = getProfileName(profile, labels.profile);
  return (
    <span className="account-switcher__details">
      <span className="account-switcher__name-line">
        <Typography as="span" role="title/sub" className="account-switcher__name">
          {name}
        </Typography>
        {profile.isPremium && <PremiumBadge tier={tier}>{tier ? premiumTierName(tier, i18n.language.startsWith('ru')) : t('account.plan.premium')}</PremiumBadge>}
        {profile.isGuest ? (
          <Typography as="span" role="meta/helper">
            {labels.guest}
          </Typography>
        ) : null}
        {active ? (
          <Typography as="span" role="meta/helper" className="account-switcher__active-badge">
            {labels.active}
          </Typography>
        ) : null}
      </span>
      <Typography as="span" role="meta/helper" className="account-switcher__summary">
        {getProfileSummary(profile, {
          noBoxes: labels.noBoxes,
          organizer: labels.organizer,
          moreOne: labels.moreOne,
          more: labels.more,
        })}
      </Typography>
    </span>
  );
}

/** Dropdown listing every profile in this browser, matching v1. */
export function AccountSwitcher({
  embedded = false,
  profiles,
  activeUserId,
  labels,
  busyProfileId,
  busyAction,
  error,
  onSelectProfile,
  onLinkGuest,
  onGoToAccount,
  onGoToAdmin,
  onAddAccount,
  onSignOut,
  onSignOutAll,
}: AccountSwitcherProps) {
  const { t } = useTranslation();
  const activeProfile = profiles.find((profile) => profile.id === activeUserId || profile.active);
  const inactiveProfiles = activeProfile
    ? profiles.filter((profile) => profile.id !== activeProfile.id)
    : profiles;
  const disabled = busyProfileId !== undefined || busyAction !== undefined;

  return (
    <Panel
      padding="none"
      elevated
      className={`account-switcher${embedded ? ' account-switcher--page' : ''}`}
      role={embedded ? 'region' : 'dialog'}
      aria-label={labels.title}
      testId="account-switcher"
    >
      {onGoToAdmin && (
        <div className="account-switcher__admin-row">
          <Button variant="blue" size="large" disabled={disabled} onClick={onGoToAdmin} testId="switcher-admin">
            {t('admin.title')}
          </Button>
        </div>
      )}
      {activeProfile ? (
        <ProfileSurface embedded={embedded}>
          <div className="account-switcher__active">
            <div className="account-switcher__active-main">
              <ProfileAvatar profile={activeProfile} active />
              <ProfileDetails profile={activeProfile} labels={labels} active />
            </div>
            <div className="account-switcher__active-actions">
              <Button
                variant="primary"
                size="small"
                disabled={disabled}
                onClick={onGoToAccount}
                testId="switcher-settings"
              >
                {labels.goToAccount}
              </Button>
              <Button
                variant="ghost"
                size="small"
                disabled={disabled}
                loading={busyAction === 'sign-out'}
                onClick={onSignOut}
                testId="switcher-sign-out"
              >
                {labels.signOut}
              </Button>
            </div>

          </div>
        </ProfileSurface>
      ) : null}

      {inactiveProfiles.length ? (
        <div className="account-switcher__profiles">
          {inactiveProfiles.map((profile) => {
            const showLink = Boolean(onLinkGuest) && !activeProfile?.isGuest && profile.isGuest;
            const name = getProfileName(profile, labels.profile);
            return (
              <ProfileSurface embedded={embedded} key={profile.id}>
                <div className="account-switcher__profile-row">
                  <button
                    type="button"
                    className="account-switcher__profile"
                    disabled={disabled}
                    onClick={() => onSelectProfile(profile.id)}
                    aria-label={labels.switchTo(name)}
                  >
                    <ProfileAvatar profile={profile} />
                    <ProfileDetails profile={profile} labels={labels} />
                    {busyProfileId === profile.id && busyAction !== 'link' ? (
                      <Spinner size="small" />
                    ) : embedded ? (
                      <span className="account-switcher__arrow" aria-hidden="true">
                        <ChevronRightIcon />
                      </span>
                    ) : null}
                  </button>
                  {showLink ? (
                    <Button
                      variant="secondary"
                      size="small"
                      className="account-switcher__link"
                      disabled={disabled}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onLinkGuest?.(profile.id);
                      }}
                      testId="profile-link-guest"
                    >
                      {labels.linkGuest}
                    </Button>
                  ) : null}
                </div>
              </ProfileSurface>
            );
          })}
        </div>
      ) : null}

      {error ? (
        <Typography as="p" role="meta/error" className="account-switcher__error">
          {error}
        </Typography>
      ) : null}

      <footer
        className={
          profiles.length > 1
            ? 'account-switcher__footer'
            : 'account-switcher__footer account-switcher__footer--single'
        }
      >
        <Button
          variant="ghost"
          size="small"
          disabled={disabled}
          onClick={onAddAccount}
          testId="switcher-add"
        >
          {labels.addAccount}
        </Button>
        {profiles.length > 1 ? (
          <Button
            variant="ghost"
            size="small"
            disabled={disabled}
            loading={busyAction === 'sign-out-all'}
            onClick={onSignOutAll}
            testId="switcher-sign-out-all"
          >
            {labels.signOutAll}
          </Button>
        ) : null}
      </footer>
    </Panel>
  );
}
